import ExtensionIcon from '@mui/icons-material/Extension';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PACKAGE_STUBS, catalogPayload } from '../../config/packageStubs';
import { isDesignMode } from '../../config/designMode';
import useApi from '../../hooks/useAPI';

const portNames = (ports) =>
  Object.keys(ports?.properties || {}).join(', ') || '—';

const Packages = () => {
  const { projects } = useApi();
  const design = isDesignMode();
  const [packages, setPackages] = useState(design ? PACKAGE_STUBS : []);
  const [isLoading, setIsLoading] = useState(!design);
  const [isSeeding, setIsSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (design) {
      setPackages(PACKAGE_STUBS);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await projects.listPackages();
      setPackages(Array.isArray(res.body) ? res.body : []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load catalog packages');
    } finally {
      setIsLoading(false);
    }
  }, [design, projects]);

  useEffect(() => {
    load();
  }, [load]);

  const seedStubs = useCallback(async () => {
    if (design) {
      setNotice('Design mode already includes these stubs in the sidebar.');
      return;
    }
    setIsSeeding(true);
    setNotice(null);
    try {
      for (const stub of PACKAGE_STUBS) {
        await projects.createOrUpdatePackage(catalogPayload(stub));
      }
      await load();
      setNotice(
        `Installed ${PACKAGE_STUBS.length} stubs. Drag them from the canvas sidebar.`
      );
    } catch (err) {
      setError(err.message || 'Failed to install stubs');
    } finally {
      setIsSeeding(false);
    }
  }, [design, load, projects]);

  const rows = useMemo(
    () =>
      [...packages].sort((a, b) => {
        const fa = (a.type || '').split('/')[0];
        const fb = (b.type || '').split('/')[0];
        if (fa !== fb) return fa.localeCompare(fb);
        return (a.name || '').localeCompare(b.name || '');
      }),
    [packages]
  );

  if (isLoading) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h4" component="h1">
              AGENTS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Catalog types you can drag onto a canvas. Install stubs to add
              agent/input · think · artifact · sink plus VPC, subnet, GKE, and
              Cloud SQL.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ExtensionIcon />}
            onClick={seedStubs}
            disabled={isSeeding}
          >
            {isSeeding ? 'Installing…' : 'Install stubs'}
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Inputs</TableCell>
                <TableCell>Outputs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((pkg) => (
                <TableRow key={pkg.id || pkg.type}>
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={pkg.type} />
                  </TableCell>
                  <TableCell>{portNames(pkg.inputs)}</TableCell>
                  <TableCell>{portNames(pkg.outputs)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    No catalog packages yet. Install stubs to play in the UI.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Container>
  );
};

export default Packages;
